import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field, inputCls } from "@/components/common";
import { pillCls } from "@/components/EduBlocks";

const EMPTY = { name: "", email: "", phone: "", instagram: "", tiktok: "", followers: "", city: "", why: "", website: "" };
const FIELDS: [keyof typeof EMPTY, string, string?][] = [["name", "Full name *"], ["email", "Email *", "email"], ["phone", "Phone", "tel"], ["city", "City"],
  ["instagram", "Instagram handle"], ["tiktok", "TikTok handle"], ["followers", "Approx. followers"]];

export function AmbassadorForm({ b }: any) {
  const [f, setF] = useState<any>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const add = Array.from(e.target.files || []).filter((x) => x.type.startsWith("image/"));
    if (add.some((x) => x.size > 10 * 1024 * 1024)) toast.error("Photos must be under 10 MB");
    setFiles((cur) => [...cur, ...add.filter((x) => x.size <= 10 * 1024 * 1024)].slice(0, 3)); e.target.value = "";
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.instagram && !f.tiktok) { toast.error("Please add your Instagram or TikTok handle"); return; }
    setBusy(true);
    try {
      const r = (await api.post("/ambassadors/apply", f)).data;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/ambassadors/apply/${r.id}/photo`, fd, { params: { t: r.token } }).catch((err) => toast.error(`${file.name}: ${errMsg(err)}`));
      }
      setDone(true);
    }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section id="apply" className="max-w-3xl mx-auto px-5 md:px-12 py-14 md:py-20" data-testid="ambassador-form-section">
      <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl text-center">{b.heading || "Apply to be an Ambassador"}</h2>
      {b.intro && <p className="mt-4 text-sm text-neutral-600 text-center leading-relaxed">{b.intro}</p>}
      {done ? (
        <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "center" })} className="mt-10 text-center border border-neutral-200 p-10" data-testid="ambassador-form-success">
          <p className="font-display text-2xl">Thank you</p>
          <p className="mt-3 text-sm text-neutral-600">{b.success_text || "We've received your application and will be in touch within five business days."}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="ambassador-form">
          {FIELDS.map(([k, l, t]) => (
            <Field key={k} label={l}><input type={t || "text"} required={k === "name" || k === "email"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} data-testid={`ambassador-${k}-input`} /></Field>
          ))}
          <div className="sm:col-span-2"><Field label="Why do you love Somnio.Co? *"><textarea required minLength={10} rows={4} value={f.why} onChange={(e) => setF({ ...f, why: e.target.value })} className={inputCls} data-testid="ambassador-why-input" /></Field></div>
          <div className="sm:col-span-2" data-testid="ambassador-photos">
            <p className="eyebrow text-[10px] text-neutral-500 mb-2">Photos of your style (optional, up to 3)</p>
            <div className="flex flex-wrap gap-3">
              {files.map((x, i) => (
                <div key={i} className="relative w-24 h-24 bg-neutral-100" data-testid={`ambassador-photo-${i}`}>
                  <img src={URL.createObjectURL(x)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setFiles(files.filter((_, k) => k !== i))} className="absolute top-1 right-1 bg-white/90 text-xs px-1.5" aria-label="Remove photo" data-testid={`ambassador-photo-remove-${i}`}>×</button>
                </div>
              ))}
              {files.length < 3 && (
                <label className="w-24 h-24 border border-dashed border-neutral-400 flex items-center justify-center text-xs text-neutral-500 cursor-pointer hover:border-neutral-900 text-center" data-testid="ambassador-photo-add">
                  + Add photo<input type="file" accept="image/*" multiple onChange={pick} className="hidden" data-testid="ambassador-photo-input" />
                </label>
              )}
            </div>
          </div>
          <input tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="hidden" aria-hidden="true" />
          <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
            <button disabled={busy} className={pillCls + " !px-8"} data-testid="ambassador-submit-button">{busy ? "Sending…" : b.submit_label || "Send Application"}</button>
            <span className="text-xs text-neutral-500">By applying you agree to our <Link to="/pages/privacy-policy" className="underline">privacy policy</Link>.</span>
          </div>
        </form>
      )}
    </section>
  );
}

export function AmbassadorShowcase({ b }: any) {
  const items = (b.items || []).filter((x: any) => x.image && x.name);
  if (!items.length) return null;
  return (
    <section className="max-w-[1300px] mx-auto px-5 md:px-12 py-14 md:py-20" data-testid="ambassador-showcase">
      {b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-3xl md:text-4xl text-center mb-12">{b.heading}</h2>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {items.map((x: any, i: number) => (
          <figure key={i} className="group" data-testid={`ambassador-card-${i}`}>
            <div className="overflow-hidden bg-neutral-100"><img src={x.image} alt={x.name} loading="lazy" className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-[1.03]" /></div>
            <p className="mt-5 font-display text-2xl">{x.name}</p>
            {x.handle && <p className="text-xs tracking-[0.15em] uppercase text-neutral-500 mt-1">{x.handle}</p>}
            {x.quote && <p className="mt-3 text-sm text-neutral-600 leading-relaxed italic">“{x.quote}”</p>}
            {x.favourite && (
              <p className="mt-3 text-sm"><span className="text-neutral-500">Favourite piece: </span>
                {x.favourite_url ? <Link to={x.favourite_url} className="underline underline-offset-4 hover:text-[var(--c-primary)]" data-testid={`ambassador-fav-${i}`}>{x.favourite}</Link> : x.favourite}</p>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
}
